import { useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { Check, X as XIcon, Loader2, Users, CalendarDays, Clock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import MediaManager from "@/components/dashboard/MediaManager";

/**
 * ReservationsTab — owner view for a restaurant business.
 *
 * Left column: incoming reservation list (grouped by status). Row actions
 * confirm/decline with an optional note that goes into the diner email.
 *
 * Right column: reusable `MediaManager` widget (hero + 2 about slots) that
 * handles uploads, AI regeneration, credit balance, and the Stripe top-up
 * modal so the same behaviour is shared across every dashboard tab.
 */
const STATUS_STYLES = {
    requested: { label: "New", cls: "bg-[#FFF3E0] border-[#E88A00] text-[#E88A00]" },
    confirmed: { label: "Confirmed", cls: "bg-[#E6F5EA] border-[#2C7A3F] text-[#2C7A3F]" },
    declined: { label: "Declined", cls: "bg-[#F5E6E6] border-[#B03A2E] text-[#B03A2E]" },
    seated: { label: "Seated", cls: "bg-[#EAEAF5] border-[#4B5FB5] text-[#4B5FB5]" },
    no_show: { label: "No show", cls: "bg-[#F0F0F0] border-[#666] text-[#666]" },
};

function StatusPill({ status }) {
    const s = STATUS_STYLES[status] || { label: status, cls: "bg-gray-100 border-gray-400 text-gray-600" };
    return (
        <span
            data-testid={`res-status-${status}`}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 border-2 rounded-full text-[10px] font-extrabold uppercase tracking-wider ${s.cls}`}
        >
            {s.label}
        </span>
    );
}

function ReservationRow({ r, onUpdate }) {
    const [busy, setBusy] = useState(null);
    const [showNote, setShowNote] = useState(false);
    const [note, setNote] = useState("");

    const update = async (status) => {
        setBusy(status);
        try {
            const { data } = await api.patch(`/reservations/${r.id}`, {
                status,
                owner_note: showNote ? note : undefined,
            });
            toast.success(
                status === "confirmed" ? "Confirmed — diner emailed" :
                status === "declined" ? "Declined — diner emailed" :
                `Marked ${status}`
            );
            onUpdate(data);
            setShowNote(false);
            setNote("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not update reservation");
        } finally {
            setBusy(null);
        }
    };

    let datePretty = r.date;
    try { datePretty = format(parseISO(r.date), "EEE, MMM d"); } catch { /* leave raw */ }

    return (
        <div
            data-testid={`reservation-row-${r.id}`}
            className="p-5 border-2 border-black rounded-xl bg-white flex flex-col sm:flex-row sm:items-start sm:gap-5"
        >
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="font-extrabold text-lg" data-testid="res-name">{r.name}</div>
                    <StatusPill status={r.status} />
                    {r.occasion && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-[#F5F0FF] border border-[#8B5CF6] text-[#5B21B6] font-semibold">
                            {r.occasion}
                        </span>
                    )}
                </div>
                <div className="mt-2 text-sm text-gray-600 flex flex-wrap gap-x-5 gap-y-1">
                    <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-4 h-4" /> {datePretty}</span>
                    <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4" /> {r.time}</span>
                    <span className="inline-flex items-center gap-1.5"><Users className="w-4 h-4" /> {r.party_size} {r.party_size === 1 ? "guest" : "guests"}</span>
                </div>
                <div className="mt-2 text-xs text-gray-500">
                    {r.business_name && <span className="font-semibold text-black">{r.business_name} · </span>}
                    {r.email} · {r.phone}
                </div>
                {r.special_requests && (
                    <div className="mt-3 p-3 rounded-lg bg-[#FFFCF5] border border-[#F0E5C8] text-sm text-gray-800">
                        <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Notes</span>
                        <div className="mt-1 whitespace-pre-wrap">{r.special_requests}</div>
                    </div>
                )}
                {r.owner_note && (
                    <div className="mt-2 text-xs text-gray-500">
                        <span className="font-semibold text-black">Your note:</span> {r.owner_note}
                    </div>
                )}
                {showNote && r.status === "requested" && (
                    <div className="mt-3">
                        <textarea
                            data-testid={`res-note-${r.id}`}
                            className="w-full p-3 border-2 border-black rounded-lg font-sans text-sm"
                            rows={3}
                            placeholder="Optional message to include in the email (e.g. 'we've got a lovely corner table saved for you')"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                        />
                    </div>
                )}
            </div>
            {r.status === "requested" && (
                <div className="mt-4 sm:mt-0 flex flex-col gap-2 min-w-[180px]">
                    {!showNote && (
                        <button
                            data-testid={`res-add-note-${r.id}`}
                            onClick={() => setShowNote(true)}
                            className="text-xs font-bold text-gray-600 hover:text-black underline underline-offset-2 text-left"
                        >
                            + Add a note for the diner
                        </button>
                    )}
                    <button
                        data-testid={`res-confirm-${r.id}`}
                        disabled={!!busy}
                        onClick={() => update("confirmed")}
                        className="px-4 py-2.5 border-2 border-black rounded-xl bg-[#2C7A3F] text-white font-extrabold flex items-center justify-center gap-2 nb-press disabled:opacity-50"
                    >
                        {busy === "confirmed" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        Confirm
                    </button>
                    <button
                        data-testid={`res-decline-${r.id}`}
                        disabled={!!busy}
                        onClick={() => update("declined")}
                        className="px-4 py-2.5 border-2 border-black rounded-xl bg-white text-[#B03A2E] font-extrabold flex items-center justify-center gap-2 nb-press disabled:opacity-50"
                    >
                        {busy === "declined" ? <Loader2 className="w-4 h-4 animate-spin" /> : <XIcon className="w-4 h-4" />}
                        Decline
                    </button>
                </div>
            )}
        </div>
    );
}

export default function ReservationsTab({ biz }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("requested");  // requested | confirmed | declined | all

    const isRestaurant = biz?.industry_template === "restaurant";

    const load = async () => {
        if (!biz?.business_id) return;
        setLoading(true);
        try {
            const params = { business_id: biz.business_id };
            if (filter !== "all") params.status = filter;
            const { data } = await api.get("/reservations", { params });
            setRows(data.reservations || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not load reservations");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [biz?.business_id, filter]);

    const onUpdate = (updated) => {
        setRows((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
        // If the filter no longer matches, refetch so the row moves out.
        if (filter !== "all" && updated.status !== filter) {
            setTimeout(load, 300);
        }
    };

    if (!biz) {
        return <div className="p-8 text-gray-500">Loading your restaurant…</div>;
    }
    if (!isRestaurant) {
        return (
            <div className="nb-card p-8 text-center bg-[#FFFCF5]" data-testid="reservations-not-restaurant">
                <div className="text-lg font-extrabold">Reservations only apply to restaurants</div>
                <div className="mt-2 text-sm text-gray-600">
                    Switch to a restaurant business or create one to start taking bookings from your website.
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-8" data-testid="reservations-tab">
            {/* Reservations list */}
            <div>
                <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
                    <div>
                        <h2 className="text-2xl font-extrabold">Reservations</h2>
                        <p className="text-sm text-gray-600 mt-1">
                            Requests from your restaurant website. Confirm or decline — the diner gets an email either way.
                        </p>
                    </div>
                    <div className="flex gap-2 flex-wrap" data-testid="reservations-filter">
                        {[
                            { id: "requested", label: "New" },
                            { id: "confirmed", label: "Confirmed" },
                            { id: "declined", label: "Declined" },
                            { id: "all", label: "All" },
                        ].map((f) => (
                            <button
                                key={f.id}
                                data-testid={`res-filter-${f.id}`}
                                onClick={() => setFilter(f.id)}
                                className={`px-3 py-1.5 rounded-full border-2 border-black text-xs font-bold ${filter === f.id ? "bg-[#111] text-white" : "bg-white text-black"}`}
                            >
                                {f.label}
                            </button>
                        ))}
                    </div>
                </div>

                {loading ? (
                    <div className="p-16 text-center text-gray-500" data-testid="reservations-loading">
                        <Loader2 className="w-6 h-6 animate-spin inline-block" />
                    </div>
                ) : rows.length === 0 ? (
                    <div className="nb-card p-10 text-center bg-white" data-testid="reservations-empty">
                        <CalendarDays className="w-8 h-8 mx-auto mb-3 text-gray-400" />
                        <div className="font-extrabold text-lg">No {filter === "all" ? "" : filter} reservations yet</div>
                        <div className="mt-1 text-sm text-gray-500">
                            When someone books a table on your published site, it shows up here first.
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4" data-testid="reservations-list">
                        {rows.map((r) => (
                            <ReservationRow key={r.id} r={r} onUpdate={onUpdate} />
                        ))}
                    </div>
                )}
            </div>

            {/* Universal media manager — hero + 2 about slots for restaurants */}
            <div className="nb-card p-5" data-testid="restaurant-media-panel">
                <MediaManager biz={biz} />
            </div>
        </div>
    );
}
